import { useState } from 'react';
import { HABITAT_INFO } from '../game/habitats';
import { RARITY, SPECIES, speciesImage, type HabitatType } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// "How Fish Beacons work": a step-by-step guide (a student asked for one),
// opened from the beacon's window, the Craft & shop list and the hotbar hint.
// The rules it explains live in game/habitats.ts (and ocean_attract on the server).
// ─────────────────────────────────────────────────────────────────────────────

type Step = { title: string; text: React.ReactNode; picture: React.ReactNode };

/** A tiny drawing of a habitat: a room of blocks with the beacon inside. */
function Room({ closed, fish }: { closed: boolean; fish: boolean }) {
  const cells: React.ReactNode[] = [];
  const W = 9, H = 6;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const edge = x === 0 || y === 0 || x === W - 1 || y === H - 1;
    const gap = !closed && x === W - 1 && (y === 2 || y === 3);
    if (edge && !gap) cells.push(<rect key={`${x}-${y}`} x={x * 20} y={y * 20} width={19} height={19} rx={3} fill="#ff7f9e" stroke="#1e2633" strokeWidth={1.5} />);
  }
  return (
    <svg viewBox="0 0 180 120" className="guide-room" aria-hidden>
      <rect x={0} y={0} width={180} height={120} fill="#bfe6ff" rx={8} />
      {cells}
      <circle cx={90} cy={60} r={11} fill="#ffd23d" stroke="#1e2633" strokeWidth={2} />
      <text x={90} y={65} textAnchor="middle" fontSize={13}>🐠</text>
      {fish && <><text x={50} y={50} fontSize={18}>🐟</text><text x={120} y={90} fontSize={18}>🐠</text></>}
      {!closed && <text x={172} y={68} textAnchor="end" fontSize={16}>⚠️</text>}
    </svg>
  );
}

export default function BeaconGuide({ onClose }: { onClose: () => void }) {
  const [i, setI] = useState(0);
  const types = Object.keys(HABITAT_INFO) as HabitatType[];
  const steps: Step[] = [
    {
      title: '1. Get a Fish Beacon',
      text: <>Buy one in <strong>Craft & shop</strong> (press <kbd>C</kbd>) for 🪙 8, or find one in a treasure chest. It shows up on your hotbar as 🐠.</>,
      picture: <div className="guide-big">🛠️ → 🐠</div>,
    },
    {
      title: '2. Build a room',
      text: <>Pick a block on your hotbar and build a room with walls all the way around, like a fish tank. You need a hammer to build. Only blocks <strong>you and your classmates placed</strong> count, not the ones that were already there.</>,
      picture: <Room closed={false} fish={false} />,
    },
    {
      title: '3. Close it in, beacon inside',
      text: <>Put the beacon <strong>inside</strong> the room, and close every gap. If there's a hole, fish won't move in. Keep the room fairly small (about 8 blocks each way from the beacon).</>,
      picture: <Room closed fish={false} />,
    },
    {
      title: '4. What you build it from decides who comes',
      text: (
        <div className="guide-types">
          {types.map(t => {
            const fish = SPECIES.filter(s => s.attracts.includes(t)).slice(0, 5);
            return (
              <div key={t} className="guide-type">
                <strong>{HABITAT_INFO[t].icon} {HABITAT_INFO[t].name}</strong>
                <span className="small muted">{HABITAT_INFO[t].blurb}</span>
                <span className="guide-fish">{fish.map(s => <img key={s.id} src={speciesImage(s.id)} alt={s.name} title={s.name} style={{ borderColor: RARITY[s.rarity].color }} />)}</span>
              </div>
            );
          })}
        </div>
      ),
      picture: null,
    },
    {
      title: '5. Wait nearby',
      text: <>New fish move in every few minutes <strong>while someone is near</strong> the beacon. A bigger habitat (more blocks) has room for more fish. Swim up to the beacon and press <kbd>E</kbd> to see who lives there, and how close you are to each kind of habitat.</>,
      picture: <Room closed fish />,
    },
    {
      title: '6. Don’t like a fish?',
      text: <>In the beacon's window, press 🌊 next to a fish to let it go. Your habitat can then attract someone new. Every fish that moves in goes in your Fishpedia.</>,
      picture: <div className="guide-big">🐟 🌊</div>,
    },
  ];
  const s = steps[i];
  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="How Fish Beacons work" onClick={onClose}>
      <div className="ocean-card tray guide" onClick={e => e.stopPropagation()}>
        <div className="row"><h3>📘 How Fish Beacons work</h3><span className="spacer" /><button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button></div>
        <div className="guide-dots" aria-hidden>{steps.map((_, k) => <span key={k} className={k === i ? 'on' : ''} />)}</div>
        <h4 style={{ margin: 0 }}>{s.title}</h4>
        {s.picture}
        <div className="guide-text">{s.text}</div>
        <div className="row">
          <button type="button" disabled={i === 0} onClick={() => setI(i - 1)}>← Back</button>
          <span className="spacer" />
          {i < steps.length - 1 ? <button type="button" className="primary" onClick={() => setI(i + 1)}>Next →</button> : <button type="button" className="primary" onClick={onClose}>Got it!</button>}
        </div>
      </div>
    </div>
  );
}
